import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '@/types';

export interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
}

export function useApi<T>(
  apiCall: () => Promise<T>,
  dependencies: any[] = []
): UseApiState<T> & { refetch: () => Promise<void> } {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const data = await apiCall();
      setState({ data, loading: false, error: null });
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: error as ApiError,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    refetch: fetchData,
  };
}

export function useApiMutation<T, P = any>(
  apiCall: (params: P) => Promise<T>
): {
  mutate: (params: P) => Promise<T>;
  loading: boolean;
  error: ApiError | null;
  data: T | null;
} {
  const [state, setState] = useState<{
    loading: boolean;
    error: ApiError | null;
    data: T | null;
  }>({
    loading: false,
    error: null,
    data: null,
  });

  const mutate = useCallback(
    async (params: P): Promise<T> => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const data = await apiCall(params);
        setState({ data, loading: false, error: null });
        return data;
      } catch (error) {
        setState({
          data: null,
          loading: false,
          error: error as ApiError,
        });
        throw error;
      }
    },
    [apiCall]
  );

  return {
    mutate,
    ...state,
  };
}
